/* __next_internal_client_entry_do_not_use__ dynamic,default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, useMediaQuery, useTheme, Chip, Tooltip } from "__barrel_optimize__?names=Box,Chip,Tooltip,useMediaQuery,useTheme!=!@mui/material";
import PageDescription from "../../components/PageDescription";
import ResponsiveWrapper from "../../components/ResponsiveWrapper";
import InvoiceForm from "../components/InvoiceForm";
import ReusableButton from "../../components/ReusableButton";
import { tokens } from "../../theme";
import { useTranslation } from "../../../../i18n/client";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useMemo, Suspense } from "react";
import { useFetchCompanyDetails, useFetchInvoiceSettings } from "../../../../../data/fetch-hooks";
import dynamicImport from "next/dynamic";
import TaxClassificationChangeNotice from "../../components/TaxClassificationChangeNotice";
export const dynamic = "force-dynamic";
const InvoiceView = dynamicImport(()=>import("../components/invoice-template/InvoiceDocumentView"), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("../components/invoice-template/InvoiceDocumentView")
            ]
    },
    ssr: false
});
const PreviewInvoice = dynamicImport(()=>import("../components/PreviewInvoice"), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("../components/PreviewInvoice")
            ]
    },
    ssr: false
});
function NewInvoiceContent() {
    var _invoiceSettings_invoiceDetailRecord, _invoiceSettings_invoiceDetailRecord1;
    const [disableSaveAsDraft, setDisableSaveAsDraft] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isPreview, setIsPreview] = useState(false);
    const [invoiceId, setInvoiceId] = useState("");
    const [invoiceValues, setInvoiceValues] = useState({});
    const { data: companyDetails = {} } = useFetchCompanyDetails();
    const { data: invoiceSettings = {} } = useFetchInvoiceSettings();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { lng } = useParams();
    // Initialize invoice language from invoice settings, not from application language
    const initialInvoiceLanguage = (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : (_invoiceSettings_invoiceDetailRecord = invoiceSettings.invoiceDetailRecord) === null || _invoiceSettings_invoiceDetailRecord === void 0 ? void 0 : _invoiceSettings_invoiceDetailRecord.invoice_language) || "de";
    const [invoiceLanguage, setInvoiceLanguage] = useState(initialInvoiceLanguage);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
    const { t } = useTranslation(lng, "invoice-form");
    const router = useRouter();
    const searchParams = useSearchParams();
    const preselectedCustomerId = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get("customerId");
    const mergedCompanyDetails = useMemo(()=>({
            ...companyDetails,
            ...invoiceSettings
        }), [
        companyDetails,
        invoiceSettings
    ]);
    const handlePreview = ()=>{
        setIsPreview(true);
    };
    const hasSavedInvoice = Boolean(invoiceId);
    // Until the first draft save, only "Save as draft" is available — no Preview.
    const isSaveAndPreview = hasSavedInvoice && !disableSaveAsDraft;
    const isPreviewActionDisabled = isSavingDraft || !hasSavedInvoice;
    const invoiceTotalAmount = Number(invoiceValues === null || invoiceValues === void 0 ? void 0 : invoiceValues.invoiceTotalAmount);
    const hasNegativeTotal = Number.isFinite(invoiceTotalAmount) && invoiceTotalAmount < 0;
    const handleModify = ()=>{
        setIsPreview(false);
        // If invoice was saved, navigate to edit page
        // Otherwise just toggle preview mode
        if (invoiceId) {
            router.push("/".concat(lng, "/application/invoices/").concat(invoiceId, "/edit"));
        }
    };
    // Update invoice language when invoice settings load asynchronously
    // This only runs if invoice settings weren't available during initialization
    useEffect(()=>{
        var _invoiceSettings_invoiceDetailRecord;
        if ((invoiceSettings === null || invoiceSettings === void 0 ? void 0 : (_invoiceSettings_invoiceDetailRecord = invoiceSettings.invoiceDetailRecord) === null || _invoiceSettings_invoiceDetailRecord === void 0 ? void 0 : _invoiceSettings_invoiceDetailRecord.invoice_language) && invoiceLanguage === "de") {
            // Only update if we're still at the default value (before customer selection or settings load)
            setInvoiceLanguage(invoiceSettings.invoiceDetailRecord.invoice_language);
        }
    }, [
        invoiceSettings === null || invoiceSettings === void 0 ? void 0 : (_invoiceSettings_invoiceDetailRecord1 = invoiceSettings.invoiceDetailRecord) === null || _invoiceSettings_invoiceDetailRecord1 === void 0 ? void 0 : _invoiceSettings_invoiceDetailRecord1.invoice_language
    ]);
    return /*#__PURE__*/ _jsxs(ResponsiveWrapper, {
        children: [
            !isPreview && /*#__PURE__*/ _jsxs(Box, {
                display: "flex",
                justifyContent: "space-between",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                        children: [
                            /*#__PURE__*/ _jsx(PageDescription, {
                                title: t("newInvoices")
                            }),
                            !disableSaveAsDraft && /*#__PURE__*/ _jsx(Chip, {
                                label: t("unsavedChanges"),
                                size: "small",
                                color: "warning",
                                variant: "outlined",
                                sx: {
                                    fontWeight: 500,
                                    fontSize: "12px"
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        display: "flex",
                        flexDirection: isMobile ? "column-reverse" : isTablet ? "row-reverse" : "row",
                        gap: isMobile ? "15px" : "20px",
                        width: isMobile ? "100%" : "auto",
                        marginBottom: isMobile ? "20px" : "",
                        children: [
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: hasNegativeTotal ? t("negativeInvoiceTotal") : "",
                                arrow: true,
                                placement: "top",
                                children: /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        width: isMobile ? "100%" : "auto"
                                    },
                                    children: /*#__PURE__*/ _jsx(ReusableButton, {
                                        name: t("saveDraft"),
                                        backgroundColor: "#F3F5FD",
                                        backgroundColorOnHover: "#F3F5FD",
                                        borderColor: colors.button.main,
                                        color: colors.button.main,
                                        type: "submit",
                                        width: "100%",
                                        form: "invoice-form",
                                        disabled: disableSaveAsDraft || isSavingDraft,
                                        loading: isSavingDraft
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                style: {
                                    width: isMobile ? "100%" : "auto"
                                },
                                children: /*#__PURE__*/ _jsx(ReusableButton, {
                                    id: isSaveAndPreview ? "invoice-save-preview" : undefined,
                                    name: isSaveAndPreview ? t("saveAndPreview") : t("preview"),
                                    backgroundColor: isPreviewActionDisabled ? colors.button.disabled : colors.primary.main,
                                    color: isPreviewActionDisabled ? colors.text.disabled : colors.text.secondary,
                                    backgroundColorOnHover: isPreviewActionDisabled ? null : colors.button.mainOnHower,
                                    width: "100%",
                                    type: isSaveAndPreview ? "submit" : "button",
                                    form: isSaveAndPreview ? "invoice-form" : undefined,
                                    onClick: isSaveAndPreview ? undefined : handlePreview,
                                    disabled: isPreviewActionDisabled,
                                    loading: isSaveAndPreview && isSavingDraft
                                })
                            })
                        ]
                    })
                ]
            }),
            isPreview ? /*#__PURE__*/ _jsx(PreviewInvoice, {
                invoiceValues: invoiceValues,
                companyDetails: companyDetails,
                invoiceSettings: invoiceSettings,
                invoiceLanguage: invoiceLanguage,
                invoiceId: invoiceId,
                handleModify: handleModify
            }) : /*#__PURE__*/ _jsxs(Box, {
                display: "flex",
                justifyContent: "space-between",
                gap: "20px",
                height: "100%",
                overflow: "hidden",
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "20px",
                            flex: "1",
                            height: "100%",
                            padding: isTablet ? "20px" : "30px",
                            overflow: "auto"
                        },
                        children: /*#__PURE__*/ _jsx(InvoiceForm, {
                            setDisableSaveAsDraft: setDisableSaveAsDraft,
                            setIsSavingDraft: setIsSavingDraft,
                            setInvoiceValues: setInvoiceValues,
                            invoiceLanguage: invoiceLanguage,
                            setInvoiceLanguage: setInvoiceLanguage,
                            disableSaveAsDraft: disableSaveAsDraft,
                            setInvoiceId: setInvoiceId,
                            preselectedCustomerId: preselectedCustomerId ? parseInt(preselectedCustomerId) : undefined,
                            onSaveAndPreview: handlePreview
                        })
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        display: isTablet ? "none" : "flex",
                        sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "20px",
                            height: "100%",
                            flex: "1",
                            overflow: "auto"
                        },
                        children: /*#__PURE__*/ _jsx(InvoiceView, {
                            waterSign: "hello",
                            invoiceValues: invoiceValues,
                            companyDetails: mergedCompanyDetails,
                            invoiceLanguage: invoiceLanguage
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(TaxClassificationChangeNotice, {})
        ]
    });
}
export default function Page() {
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: /*#__PURE__*/ _jsx("div", {
            children: "Loading..."
        }),
        children: /*#__PURE__*/ _jsx(NewInvoiceContent, {})
    });
}
